import type { AxiosInstance } from 'axios';
import { format } from 'date-fns';

import { waitPromise } from '../utils';

enum IndexType {
  time = 'DATE_TIME',
  depth = 'MEASURED_DEPTH',
}

enum LogDataType {
  DATE_TIME = 'DATE_TIME',
  DOUBLE = 'DOUBLE',
  LONG = 'LONG',
  STRING = 'STRING',
  UNKNOWN = 'UNKNOWN',
}

export type CurveInfo = {
  id: number;
  mnemonic: string;
  indexType: IndexType;
  unit: string;
  lastValue: string;
  classWitsml: 'RIGIS' | 'COMMENTS' | 'FREEZE' | 'unknown' | null;
  typeLogData: LogDataType;
  minValue: number;
  maxValue: number;
  minKey: number;
  maxKey: number;
  scaleSet: number[];
  ixUom: 'm' | 'ft' | 's';
  status: 'IN_QUEUE' | 'IN_PROGRESS' | 'BLOCKED' | 'LOADED' | 'UNKNOWN';
};

export type CurvePoint = { key: number; value: number };
export type LinearChunks = [number, number, ...number[]];

export class CurvesCacheService {
  constructor(private axios: AxiosInstance) {}

  async getCurve(id: number, ms = 800, abortController?: AbortController): Promise<CurveInfo> {
    const response = await this.axios.get<CurveInfo>(`/curve/${id}`, { signal: abortController?.signal });
    let { data } = response;

    if (response.status === 202) {
      if (ms > 800 * 8) {
        throw new Error('Time out');
      }

      await waitPromise(ms);
      abortController?.signal.throwIfAborted();
      data = await this.getCurve(id, ms * 2, abortController);
    }

    return CurvesCacheService.normalizeCurveInfo(data);
  }

  async getTimeCoordsAsLinear(id: number, scale = 1, from?: Date, to?: Date, abortController?: AbortController) {
    const response = await this.axios.get<LinearChunks[]>(`/curve/${id}/coordinates/by-time/linear`, {
      params: {
        from: from && format(from, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        to: to && format(to, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        scale,
      },
      signal: abortController?.signal,
    });

    if (response.status === 202) {
      return { status: 'downloading' as const };
    }
    return response.data;
  }

  static linearSubChunkToPoints(subChunk: LinearChunks): CurvePoint[] {
    const [start, period, ...values] = subChunk;
    return values.map((value, index) => ({ key: start + index * period, value }));
  }

  static normalizeCurveInfo(info: CurveInfo): CurveInfo {
    if (info.ixUom == null) {
      if (info.indexType === IndexType.depth) {
        info.ixUom = 'm';
      } else {
        info.ixUom = 's';
      }
    }

    return info;
  }
}
